import React, { useState, useEffect, useRef } from 'react';
import { ConfirmRemoveBundleDialog } from '@/components/ConfirmRemoveBundleDialog';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Scan, Check, X, AlertTriangle, RefreshCw, Loader2, Truck, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

const GoodsTransferBundleReceiptEntry = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [scannedBundles, setScannedBundles] = useState([]);
  const [bundleInput, setBundleInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null);
  const bundleInputRef = useRef(null);

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft, setActiveDraftId,
  } = useOperationDraft({ moduleKey: 'section:Goods Transfer', operationType: 'bundle_receipt' });

  useEffect(() => {
    if (!selectedDocument) return;
    const docId = selectedDocument.transfer_id || selectedDocument.entry_id;
    autoSave({
      label: `Goods Transfer — ${docId}`,
      bundleCount: scannedBundles.length,
      documentId: docId,
      documentNo: docId,
      payload: { document: selectedDocument, scanned: scannedBundles },
    });
  }, [scannedBundles, selectedDocument, autoSave]);

  const handleResumeDraft = async (draftId) => {
    try {
      const doc = await loadDraft(draftId);
      const p = doc.payload || {};
      setSelectedDocument(p.document || null);
      setScannedBundles(p.scanned || []);
      setBundleInput('');
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    if (selectedDocument && bundleInputRef.current) {
      bundleInputRef.current.focus();
    }
  }, [selectedDocument]);

  // Re-focus the scan input every time a scan completes (isScanning → false)
  // or a bundle is added/removed. The native disabled toggle on the <Input>
  // drops focus, so a useEffect after the re-render is the only reliable way
  // to keep the scanner gun pointed at the right field.
  useEffect(() => {
    if (!isScanning && selectedDocument && bundleInputRef.current) {
      bundleInputRef.current.focus();
    }
  }, [isScanning, scannedBundles.length, selectedDocument]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      // Fetch incoming transfers/consignments to Goods Transfer section
      const response = await apiClient.get('/consignments/section/Goods Transfer/incoming');
      
      // Filter to only show received/accepted items that need bundle receipt
      // Exclude documents where bundle receipt is already completed
      const docsForReceipt = (response.data.consignments || []).filter(c => 
        (c.status === 'received' || c.status === 'accepted' || c.status === 'in_transit') &&
        !c.bundle_receipt_completed
      );
      
      setDocuments(docsForReceipt);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents for receipt');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDocument = (docId) => {
    const existing = (drafts || []).find((d) => d.document_id === docId);
    if (existing) { handleResumeDraft(existing.draft_id); return; }
    const doc = documents.find(d => (d.transfer_id || d.entry_id) === docId);
    setResumedDraft(null);
    setSelectedDocument(doc);
    setScannedBundles([]);
    setBundleInput('');
    setActiveDraftId(null);
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setSelectedDocument(null);
    setScannedBundles([]);
    setBundleInput('');
  };

  const handleScanBundle = async () => {
    if (!bundleInput.trim()) return;
    
    const barcode = bundleInput.trim().toUpperCase();
    
    // Check if already scanned
    if (scannedBundles.some(b => b.bundle_barcode === barcode)) {
      toast.error('Bundle already scanned');
      setBundleInput('');
      bundleInputRef.current?.focus();
      return;
    }
    
    setIsScanning(true);
    try {
      // Get document bundles
      const docBundles = selectedDocument.bundles || selectedDocument.bundle_codes || [];
      let bundleBarcodes = [];
      
      if (docBundles.length > 0) {
        if (typeof docBundles[0] === 'string') {
          bundleBarcodes = docBundles;
        } else {
          bundleBarcodes = docBundles.map(b => b.bundle_barcode || b);
        }
      }
      
      // Check if bundle belongs to this document
      if (bundleBarcodes.length > 0 && !bundleBarcodes.includes(barcode)) {
        toast.error(`Bundle ${barcode} is not part of this document`);
        setBundleInput('');
        setIsScanning(false);
        setTimeout(() => bundleInputRef.current?.focus(), 50);
        return;
      }
      
      // Validate bundle exists in system
      const validateResponse = await apiClient.get(`/goods-transfer/bundles/validate/${barcode}`);
      
      if (validateResponse.data.valid) {
        setScannedBundles(prev => [...prev, {
          bundle_barcode: barcode,
          item_barcode: validateResponse.data.item_barcode || '-',
          usn_count: validateResponse.data.usn_count || 0,
          status: 'scanned',
          scanned_at: new Date().toISOString()
        }]);
        toast.success(`Bundle ${barcode} scanned`);
      } else {
        toast.error(validateResponse.data.message || 'Invalid bundle');
      }
    } catch (error) {
      // If validation endpoint doesn't exist, just add the bundle
      setScannedBundles(prev => [...prev, {
        bundle_barcode: barcode,
        status: 'scanned',
        scanned_at: new Date().toISOString()
      }]);
      toast.success(`Bundle ${barcode} scanned`);
    } finally {
      setBundleInput('');
      setIsScanning(false);
      // Defer refocus: the scan <Input> is disabled={isScanning}, so a
      // synchronous focus() before React re-enables it is a no-op.
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const [removeBundleTarget, setRemoveBundleTarget] = useState(null);

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.bundle_barcode !== barcode));
  };

  const handleSubmitReceipt = async () => {
    if (scannedBundles.length === 0) {
      toast.error('Please scan at least one bundle');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const docId = selectedDocument.transfer_id || selectedDocument.entry_id;
      
      await apiClient.post('/internal-transfer/complete-bundle-receipt', {
        transfer_id: docId,
        section_name: 'Goods Transfer',
        bundles: scannedBundles.map(b => b.bundle_barcode)
      });
      
      toast.success(`Bundle receipt completed for ${scannedBundles.length} bundles`);
      clearDraft();
      setSelectedDocument(null);
      setScannedBundles([]);
      setResumedDraft(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error submitting receipt:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete bundle receipt');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDocBundleCount = (doc) => {
    const bundles = doc.bundles || doc.bundle_codes || [];
    return bundles.length;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="gt-bundle-receipt-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/sections/goods-transfer')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Bundle Receipt Entry</h1>
              <p className="text-sm text-[#9CA3AF]">Goods Transfer Section</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchDocuments}
              className="text-[#1A1F2E] border-white/30"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {resumedDraft && selectedDocument && (
          <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} />
        )}
        {isLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading documents...</span>
            </CardContent>
          </Card>
        ) : !selectedDocument ? (
          /* Document Selection */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                Select Document for Bundle Receipt
              </CardTitle>
              <CardDescription>
                Choose an incoming transfer or consignment to receive bundles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No pending documents for bundle receipt</p>
                  <p className="text-sm mt-1">Bundles will appear here when transfers arrive</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => {
                    const docId = doc.transfer_id || doc.entry_id;
                    const bundleCount = getDocBundleCount(doc);
                    
                    return (
                      <div 
                        key={docId}
                        className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleSelectDocument(docId)}
                        data-testid={`doc-${docId}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {doc.transfer_id ? `Transfer: ${doc.transfer_id}` : `Consignment: ${doc.entry_id}`}
                            </p>
                            <p className="text-sm text-gray-500">
                              From: {doc.from_section || doc.source_section || '-'} | 
                              Bundles: {bundleCount}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatDate(doc.created_at || doc.received_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {(drafts || []).some((d) => d.document_id === docId) && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`doc-draft-${docId}`}>
                                Draft
                              </Badge>
                            )}
                            <Badge 
                              variant={doc.status === 'received' ? 'default' : 'outline'}
                              className="capitalize"
                            >
                              {doc.status}
                            </Badge>
                            <Button size="sm" variant="outline">
                              Select
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Bundle Scanning */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Scanning */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Scan className="w-5 h-5 text-blue-600" />
                      Scan Bundles
                    </CardTitle>
                    <CardDescription>
                      Document: {selectedDocument.transfer_id || selectedDocument.entry_id}
                    </CardDescription>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setSelectedDocument(null);
                      setScannedBundles([]);
                      setResumedDraft(null);
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    ref={bundleInputRef}
                    value={bundleInput}
                    onChange={(e) => setBundleInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleScanBundle()}
                    placeholder="Scan bundle barcode..."
                    className="font-mono"
                    disabled={isScanning}
                    data-testid="bundle-scan-input"
                  />
                  <Button 
                    onClick={handleScanBundle}
                    disabled={isScanning || !bundleInput.trim()}
                  >
                    {isScanning ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Scan className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-700">{scannedBundles.length}</p>
                    <p className="text-sm text-blue-600">Scanned</p>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-gray-700">{getDocBundleCount(selectedDocument)}</p>
                    <p className="text-sm text-gray-600">Expected</p>
                  </div>
                </div>

                {/* Submit Button */}
                <Button 
                  className="w-full"
                  onClick={handleSubmitReceipt}
                  disabled={scannedBundles.length === 0 || isSubmitting}
                  data-testid="submit-receipt-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete Receipt ({scannedBundles.length} bundles)
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Right: Scanned List */}
            <Card>
              <CardHeader>
                <CardTitle>Scanned Bundles</CardTitle>
                <CardDescription>
                  {scannedBundles.length} of {getDocBundleCount(selectedDocument)} bundles received
                </CardDescription>
              </CardHeader>
              <CardContent>
                {scannedBundles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No bundles scanned yet</p>
                    <p className="text-sm">Scan bundles to receive them</p>
                  </div>
                ) : (
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Bundle</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead>USNs</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {scannedBundles.map((bundle, idx) => (
                          <TableRow key={bundle.bundle_barcode}>
                            <TableCell className="font-mono text-sm">
                              {bundle.bundle_barcode}
                            </TableCell>
                            <TableCell className="text-sm">
                              {bundle.item_barcode || '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {bundle.usn_count || '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRemoveBundleTarget(bundle.bundle_barcode)}
                                data-testid={`remove-bundle-${bundle.bundle_barcode}`}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      <ConfirmRemoveBundleDialog
        barcode={removeBundleTarget}
        onConfirm={() => { handleRemoveBundle(removeBundleTarget); setRemoveBundleTarget(null); }}
        onCancel={() => setRemoveBundleTarget(null)}
      />
    </div>
  );
};

export default GoodsTransferBundleReceiptEntry;
